
import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollAnimationOptions = {}
) {
  const { threshold = 0.1, rootMargin = '0px 0px -50px 0px', once = false } = options;
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}

export function useStaggerAnimation<T extends HTMLElement = HTMLDivElement>(
  count: number,
  options: UseScrollAnimationOptions = {}
) {
  const { threshold = 0.05, rootMargin = '0px 0px -50px 0px', once = false } = options;
  const ref = useRef<T>(null);
  const safeCount = Math.max(0, Math.floor(count));
  const [visibleItems, setVisibleItems] = useState<boolean[]>(() =>
    new Array(safeCount).fill(false)
  );

  useEffect(() => {
    setVisibleItems(new Array(safeCount).fill(false));
  }, [safeCount]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined' || safeCount === 0) {
      setVisibleItems(new Array(safeCount).fill(true));
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          for (let i = 0; i < safeCount; i++) {
            const delay = i * 120;
            setTimeout(() => {
              setVisibleItems((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, delay);
          }
          if (once) observer.unobserve(el);
        } else if (!once) {
          setVisibleItems(new Array(safeCount).fill(false));
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [safeCount, threshold, rootMargin, once]);

  return { ref, visibleItems };
}
